import Card from "@/components/Card";
import SectionHeader from "@/components/SectionHeader";
import SkeletonRow from "@/components/SkeletonRow";
import Stepsbar from "@/components/Stepsbar";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import useDataPendaftaran from "@/hooks/useDataPendaftaran";
import { useMultistepForm } from "@/hooks/useMultistepForm";
import useScreen from "@/hooks/useScreen";
import useSessionStorage from "@/hooks/useSessionStorage";
import useTimePeriode from "@/hooks/useTimePeriode";
import { useUser } from "@/hooks/useUser";
import api from "@/lib/axios";
import { formatDate } from "@/lib/utils";
import { getDataStep, getListBerkasPenerimaan } from "@/services/getData";
import {
  BiodataSateStudentExchange,
  DataSiswa,
  Step,
  SysBerkas,
} from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { ChevronLeft, ChevronRight, Loader2, Save } from "lucide-react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { IoWarning } from "react-icons/io5";
import { LuChevronRight } from "react-icons/lu";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import Biodata from "./partial/biodata/Biodata";
import KartuPeserta from "./partial/kartu-peserta/KartuPeserta";
import UnggahBerkas from "./partial/unggah-berkas/UnggahBerkas";

const saveBiodata = async (form: FormData) => {
  const response = await api.post(
    "/siswa/penerimaan/smbjm/biodata/student-exchange/update",
    form,
    { withCredentials: true },
  );
  return response.data;
};

export default function PenerimaanStudentExchange() {
  const { user } = useUser();
  const { isMobile } = useScreen();
  const [searchParams] = useSearchParams();
  // const sessionPeriode?.id_sys_periode = searchParams.get("periode");
  // const stepParams = searchParams.get("step");
  const { isValidPendaftaran } = useDataPendaftaran();
  const {
    isPendingPeriode,

    dataPeriode,
    timePassedActive,
    dataPeriodeActive,
  } = useTimePeriode();

  const navigate = useNavigate();

  const [sessionPeriode] = useSessionStorage("sessionPeriode", {
    id_sys_periode: "",
  });

  const { data: dataStep } = useQuery({
    queryKey: ["dataStep", user?.id, sessionPeriode?.id_sys_periode],
    queryFn: () => getDataStep(sessionPeriode?.id_sys_periode || ""),
  });
  const idDataPendaftaran = dataStep?.id_data_pendaftaran;

  const { data: dataBerkas } = useQuery({
    queryKey: ["listDataBerkas", sessionPeriode?.id_sys_periode],
    queryFn: () =>
      getListBerkasPenerimaan(sessionPeriode?.id_sys_periode || ""),
  });

  const [isPendingSteps, setisPendingSteps] = useState(false);
  const [steps, setSteps] = useState<Step[]>([
    {
      number: 1,
      text: "Personal Information",
      link: "#",
      status: "active",
      tabStatus: "active",
    },
    { number: null, text: null, link: null, status: "nonactive" },
    {
      number: 2,
      text: "Documents",
      link: "#",
      status: "nonactive",
      tabStatus: "nonactive",
    },
    {
      number: null,
      text: null,
      link: null,
      status: "nonactive",
    },
    {
      number: 3,
      text: "Validation & Participant Card",
      link: "#",
      status: "nonactive",
      tabStatus: "nonactive",
    },
  ]);
  const [currentIndexDisable, setCurrentIndexDisable] = useState(1);
  const [listPanduanPenerimaan, setListPanduanPenerimaan] = useState([
    {
      title: "Personal Information",
      contents: [
        {
          title: "Required Documents",
          instructions: ["Foto"],
        },
        {
          title: "Filling Instructions",
          instructions: [
            "Click the field you want to fill in",
            "Fill in the data correctly according to the requested field",
          ],
        },
      ],
    },
    {
      title: "Documents",
      contents: [
        {
          title: "Required Documents",
          instructions: [
            "Can be seen in the table. (Documents labeled with (*Required) are mandatory to upload.)",
          ],
        },
        {
          title: "Filling Instructions",
          instructions: [
            "For documents with a format (indicated by a Download Button in the Format column), download it first",
            "Press the 'Upload' button",
            "Select the document file",
            "Fill in the document number if applicable",
            "Press the 'Save Document File' button",
          ],
        },
      ],
    },
    {
      title: "Participant Card",
      contents: [
        {
          title: "Instructions",
          instructions: [
            "Make sure all registration data has been filled in correctly",
            "Press 'Validate Registration' to validate your registration",
            "Download the participant card by pressing the 'Download' button",
          ],
        },
      ],
    },
  ]);

  const [formBiodata, setFormBiodata] = useState<BiodataSateStudentExchange>({
    id_sys_periode: sessionPeriode?.id_sys_periode || "",
    id_data_siswa: 0,
    id_ref_agama: "",
    id_ref_negara: "",
    nama_lengkap: "",
    nama_ibu_kandung: "",
    handphone: "",
    tanggal_lahir: "",
    tempat_lahir: "",
    jenis_kelamin: "L",
    ukuran_baju: "",
    berat_badan: 0,
    tinggi_badan: 0,
    jalan: "",
    jalan_domisili: "",
    kode_pos: "",
    kelurahan: "",
    dusun: "",
    pilihan_lokasi_kuliah: "singaraja",
    asal_sekolah: "",
    asal_jurusan_sekolah_internasional: "",
    asal_pt: "",
    asal_pt_na: "",
    asal_prodi: "",
    asal_prodi_na: "",
    is_bekerja: undefined,
    tempat_bekerja: "",
    penghasilan_bekerja: "",
    tahun_ijazah_terakhir: "",
    nilai_IPK: "",
    accrediting_organization_pt: "",
    accrediting_organization_prodi: "",

    id_ref_daftar_program: "",
    nama_depan: "",
    nama_belakang: "",
    nomor_viza: "",
    is_alergi_obat: false,
    golongan_darah: "",
    kontak_darurat_hubungan: "",
    kontak_darurat_nama: "",
    kontak_darurat_hp: "",
    provinsi_state: "",
  });
  const [file, setFile] = useState<File | null>(null);

  const queryClient = useQueryClient();
  const {
    mutate,
    isPending: isPendingSaveBiodata,
    error,
  } = useMutation({
    mutationFn: saveBiodata,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["dataStep"],
      });
      toast.success(data?.message || "Success.");
    },
    onError: (
      error: AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>,
    ) => {
      const err = error as AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>;
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    },
  });

  const {
    currentStep,
    firstStep,
    lastStep,
    backStep,
    goToStep,
    currentIndex,
    currentStepNumber,
  } = useMultistepForm([
    <Biodata
      form={formBiodata}
      setForm={setFormBiodata}
      file={file}
      setFile={setFile}
      error={error}
    />,
    <UnggahBerkas idDataPendaftaran={idDataPendaftaran} />,
    <KartuPeserta idDataPendaftaran={idDataPendaftaran} />,
  ]);

  const [showPanduan, setShowPanduan] = useState(false);

  const handleSaveBiodata = async () => {
    const formData = new FormData();

    Object.keys(formBiodata).forEach((key) => {
      const value = formBiodata[key as keyof DataSiswa];
      if (Array.isArray(value)) {
        // Handle array (kebutuhan_khusus)
        value.forEach((item, index) => {
          if (item !== null && item !== undefined) {
            formData.append(`${key}[${index}]`, String(item)); // Konversi ke string menggunakan String()
          }
        });
      } else {
        // Handle nilai non-array
        if (value !== null && value !== undefined) {
          formData.append(key, String(value)); // Konversi ke string menggunakan String()
        }
      }
    });
    if (file) formData.append("foto", file);

    mutate(formData);
  };

  const handleNextStep = () => {
    switch (currentStepNumber) {
      case 1:
        if (dataStep?.data_diri === 1) {
          goToStep(2);
          searchParams.set("step", "2");
          navigate(`${location.pathname}?${searchParams.toString()}`);
        }
        break;
      case 2:
        if (dataStep?.data_berkas === 1) {
          goToStep(3);
          searchParams.set("step", "3");
          navigate(`${location.pathname}?${searchParams.toString()}`);
        }
        break;
    }
  };

  useEffect(() => {
    setisPendingSteps(true);
    if (!dataStep) return;

    setSteps((prev) => {
      const updatedSteps = [...prev];

      // Helper function untuk update status connector (null steps)
      const updateConnectorStatus = (index: number) => {
        if (index <= 0 || index >= updatedSteps.length) return;

        const prevStep = updatedSteps[index - 1];
        if (updatedSteps[index].number === null) {
          updatedSteps[index].status =
            prevStep.status === "completed" ? "completed" : "nonactive";
        }
      };

      // Update main steps status
      updatedSteps.forEach((step, index) => {
        if (!step.text) return; // Skip connector steps

        switch (step.text) {
          case "Personal Information":
            if (dataStep?.data_diri === 1) {
              step.status = "completed";
              step.tabStatus = "active";
              if (updatedSteps[index + 2]) {
                updatedSteps[index + 2].status = "active";
                updatedSteps[index + 2].tabStatus = "active";
              }
              goToStep(2);
              setCurrentIndexDisable(2);
            }
            break;

          case "Documents":
            if (dataStep?.data_berkas === 1) {
              step.status = "completed";
              step.tabStatus = "active";
              if (updatedSteps[index + 2]) {
                updatedSteps[index + 2].status = "active";
                updatedSteps[index + 2].tabStatus = "active";
              }
              goToStep(3);
              setCurrentIndexDisable(3);
            }
            break;
          case "Validation & Participant Card":
            if (dataStep?.valid_is_pendaftaran === 1) {
              step.status = "completed";
              step.tabStatus = "active";
              if (updatedSteps[index + 2]) {
                updatedSteps[index + 2].status = "active";
                updatedSteps[index + 2].tabStatus = "active";
              }
              // goToStep(4)
            }
            break;
        }
      });

      // Update connector steps
      updatedSteps.forEach((_, index) => {
        updateConnectorStatus(index);
      });

      return updatedSteps;
    });

    setisPendingSteps(false);
  }, [goToStep, dataStep]);

  useEffect(() => {
    setSteps((prev) =>
      prev.map((item) => {
        if (item.number != currentStepNumber)
          return { ...item, tabStatus: "nonactive" };
        return { ...item, tabStatus: "active" };
      }),
    );
  }, [currentStepNumber]);

  useEffect(() => {
    if (isMobile) setShowPanduan(false);
    else setShowPanduan(true);
  }, [isMobile]);

  useEffect(() => {
    if (!dataBerkas) return;

    const listNamaBerkas = dataBerkas?.listSysBerkas?.map((row: SysBerkas) => {
      return row.jenis_berkas?.nama_jenis_berkas;
    });

    setListPanduanPenerimaan((prev) =>
      prev?.map((value) => {
        if (value.title.startsWith("Berkas")) {
          return {
            ...value,
            contents: value.contents.map((content) => {
              if (content.title.startsWith("Berkas")) {
                return {
                  ...content,
                  instructions: listNamaBerkas,
                };
              }
              return content;
            }),
          };
        }
        return value;
      }),
    );
  }, [dataBerkas]);

  // useEffect(() => {
  //   if (timePassed.waktu_pengumuman) {
  //     flushSync(() => {
  //       setSessionPeriode({
  //         id_sys_periode: dataPeriode?.id_sys_periode || "",
  //       });
  //     });
  //     navigate(`/pengumuman`);
  //   }
  // }, [timePassed, navigate, sessionPeriode?.id_sys_periode]);

  const isDisableInput =
    isValidPendaftaran || timePassedActive?.waktu_selesai_daftar_valid_selesai;

  if (isPendingPeriode) {
    return (
      <div className="flex flex-col gap-y-3">
        {Array(10)
          .fill(null)
          .map((_, index) => {
            return <SkeletonRow key={index} />;
          })}
      </div>
    );
  }

  if (
    dataPeriode &&
    dataPeriode?.jalur_jenis?.nama_jalur_jenis_slug !== "studentexchange"
  ) {
    return <Navigate to="/dashboard" />;
  }

  return (
    <main>
      <Alert
        variant={"warning"}
        className="mb-6 border-l-4 py-4 px-6 shadow-sm bg-amber-50/50 dark:bg-amber-950/20"
      >
        <IoWarning className="h-5 w-5 text-amber-600 mt-1" />
        <AlertTitle className="text-amber-800 dark:text-amber-400 font-bold text-lg mb-2">
          Important Notice
        </AlertTitle>

        <AlertDescription className="space-y-4">
          {/* Registration Deadline Status */}
          <div className="flex flex-col gap-1 border-b border-amber-200 dark:border-amber-800 pb-3">
            {timePassedActive?.waktu_selesai_daftar_valid_selesai ? (
              <div className="flex items-center gap-2 text-rose-700 dark:text-rose-400">
                <span className="h-2 w-2 rounded-full bg-rose-500 animate-pulse" />
                <p className="font-medium">
                  Registration closed on:{" "}
                  <span className="font-bold underline">
                    {dataPeriodeActive?.waktu_selesai_daftar_valid &&
                      formatDate(
                        dataPeriodeActive?.waktu_selesai_daftar_valid,
                        undefined,
                        "en-US",
                      )}
                  </span>
                </p>
              </div>
            ) : (
              <p className="text-amber-900 dark:text-slate-300">
                To complete your registration, please ensure you have{" "}
                <span className="font-bold text-amber-700 dark:text-amber-400 uppercase tracking-tight">
                  Validated
                </span>{" "}
                your data and obtained a{" "}
                <span className="font-semibold italic">Participant Card</span>.
              </p>
            )}
          </div>
        </AlertDescription>
      </Alert>

      <SectionHeader title={`Admission - Student Exchange`} />

      <div className="flex lg:flex-col flex-row lg:gap-10">
        <Stepsbar
          steps={steps}
          goToStep={goToStep}
          isPending={isPendingSteps}
        />
        <div className="flex-1 h-screen overflow-auto">
          <div className="flex lg:flex-row flex-col lg:gap-5 gap-3">
            {currentStepNumber !== 2 && (
              <Card className={`${showPanduan ? "lg:w-[20rem]" : ""} h-fit`}>
                <div className="flex justify-between">
                  {showPanduan && (
                    <h1 className="mb-5 font-bold text-xl">Instructions</h1>
                  )}
                  <div className="flex items-center flex-col gap-y-3">
                    {!showPanduan && (
                      <span className="text-sm font-bold">Instructions</span>
                    )}
                    <Button
                      variant={"outline"}
                      size={"icon"}
                      onClick={() => setShowPanduan((show) => !show)}
                    >
                      <LuChevronRight
                        className={`${
                          showPanduan ? "rotate-90" : "rotate-0"
                        } duration-300`}
                      />
                    </Button>
                  </div>
                </div>
                <section className="flex flex-col gap-y-3">
                  {showPanduan &&
                    listPanduanPenerimaan.map((panduan, index) => {
                      return (
                        <div key={index}>
                          <Accordion type="single" collapsible>
                            <AccordionItem value="item-1">
                              <AccordionTrigger className="text-start">
                                {panduan.title}
                              </AccordionTrigger>
                              <AccordionContent>
                                <div className="flex flex-col gap-1">
                                  {panduan.contents.map((content) => {
                                    return (
                                      <div key={content.title}>
                                        <div className="flex items-center gap-1">
                                          <span>-</span>
                                          <h1 className="font-semibold">
                                            {content.title}
                                          </h1>
                                        </div>
                                        <ul>
                                          {content.instructions.map(
                                            (instruction, indexInstruction) => {
                                              return (
                                                <li
                                                  key={indexInstruction}
                                                  className="text-muted-foreground"
                                                >
                                                  <span>
                                                    {indexInstruction + 1}.{" "}
                                                  </span>
                                                  {instruction}
                                                </li>
                                              );
                                            },
                                          )}
                                        </ul>
                                      </div>
                                    );
                                  })}
                                </div>
                              </AccordionContent>
                            </AccordionItem>
                          </Accordion>
                        </div>
                      );
                    })}
                </section>
              </Card>
            )}

            <div className="flex-1 overflow-auto">
              {currentStep}

              {/* Wrapper Navigation */}
              <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-800 flex flex-col-reverse sm:flex-row justify-between items-center gap-4">
                {/* Tombol KIRI (Kembali) */}
                <div className="w-full sm:w-auto">
                  {!firstStep && (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={backStep}
                      className="w-full sm:w-auto text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100 transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4 mr-2" />
                      Back
                    </Button>
                  )}
                </div>

                {/* Tombol KANAN (Group Aksi) */}
                <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                  {/* Tombol Simpan (Hanya di step awal & bisa diedit) */}
                  {firstStep && !isDisableInput && (
                    <Button
                      type="button"
                      variant="secondary" // Menggunakan varian secondary agar tidak rebutan fokus dengan 'Berikutnya'
                      className="w-full sm:w-auto bg-emerald-50 text-emerald-600 hover:bg-emerald-100 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800"
                      disabled={isPendingSaveBiodata}
                      onClick={handleSaveBiodata}
                    >
                      {isPendingSaveBiodata ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Save
                        </>
                      )}
                    </Button>
                  )}

                  {/* Tombol Berikutnya */}
                  {!lastStep && (
                    <Button
                      type="button"
                      variant="default" // Primary action
                      className="w-full sm:w-auto"
                      disabled={currentIndex === currentIndexDisable - 1} // Gunakan properti disabled asli
                      onClick={handleNextStep}
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
